import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import Landing from "@/pages/Landing";
import Dashboard from "@/pages/Dashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import PartnerLogin from "@/pages/PartnerLogin";
import PartnerDashboard from "@/pages/PartnerDashboard";
import PolicyPage from "@/pages/PolicyPage";
import ResetPassword from "@/pages/ResetPassword";
import EmailSignaturePage from "@/pages/EmailSignaturePage";
import { Toaster } from "@/components/ui/sonner";
import GlitchIntro from "@/components/GlitchIntro";
import RouteWipe from "@/components/RouteWipe";
import ForcePasswordChange from "@/components/ForcePasswordChange";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { initAnalytics } from "@/analytics";
import { usePresenceHeartbeat } from "@/hooks/usePresenceHeartbeat";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

function App() {
  const [token, setToken] = useState(localStorage.getItem('lottery_token'));
  const [user, setUser] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  // True while we have a token but haven't yet resolved /auth/me — prevents
  // redirect from "/" → "/dashboard" or "/admin" that strips query strings.
  const [authLoading, setAuthLoading] = useState(!!localStorage.getItem('lottery_token'));

  const toggleMute = () => setIsMuted(prev => !prev);

  const logout = useCallback(() => {
    localStorage.removeItem('lottery_token');
    setToken(null);
    setUser(null);
    setAuthLoading(false);
  }, []);

  const fetchUser = useCallback(async () => {
    try {
      const response = await fetch(`${API}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setUser(data);
      } else {
        logout();
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setAuthLoading(false);
    }
  }, [token, logout]);

  useEffect(() => {
    if (token) {
      setAuthLoading(true);
      fetchUser();
    } else {
      setAuthLoading(false);
    }
  }, [token, fetchUser]);

  // Initialize Google Analytics once on first mount.
  useEffect(() => {
    initAnalytics();
  }, []);

  // Anonymous realtime presence heartbeat (no PII).
  usePresenceHeartbeat(30000);

  const login = (newToken, userData) => {
    localStorage.setItem('lottery_token', newToken);
    setToken(newToken);
    setUser(userData);
  };

  return (
    <LanguageProvider>
    <div className="App">
      <GlitchIntro />
      <BrowserRouter>
        <RouteWipe />
        <Routes>
          <Route path="/" element={
            authLoading ? null :
              (token && user ?
                (user.role === 'admin' ? <Navigate to="/admin" /> :
                 user.role === 'affiliate' ? <Navigate to="/partner" /> :
                 <Navigate to="/dashboard" />)
                : <Landing onLogin={login} isMuted={isMuted} toggleMute={toggleMute} />)
          } />
          <Route path="/legal/:slug" element={<PolicyPage />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/email-signature" element={<EmailSignaturePage />} />
          <Route path="/partner/login" element={
            authLoading ? null :
              (token && user && user.role === 'affiliate' ? <Navigate to="/partner" /> :
               <PartnerLogin onLogin={login} />)
          } />
          <Route path="/partner" element={
            authLoading ? null :
              (token && user && user.role === 'affiliate' ?
                <PartnerDashboard user={user} token={token} onLogout={logout} /> :
                <Navigate to="/partner/login" />)
          } />
          <Route path="/dashboard" element={
            authLoading ? null :
              (token && user ?
                (user.role === 'affiliate' ? <Navigate to="/partner" /> :
                 user.role === 'admin' ? <Navigate to="/admin" /> :
                 <Dashboard user={user} token={token} onLogout={logout} onUpdateUser={fetchUser} isMuted={isMuted} toggleMute={toggleMute} />)
                : <Navigate to="/" />)
          } />
          <Route path="/admin" element={
            authLoading ? null :
              (token && user && user.role === 'admin' ? <AdminDashboard user={user} token={token} onLogout={logout} isMuted={isMuted} toggleMute={toggleMute} /> : <Navigate to="/" />)
          } />
        </Routes>
        {token && user && user.mustChangePassword && (
          <ForcePasswordChange
            user={user}
            token={token}
            onComplete={fetchUser}
            onLogout={logout}
          />
        )}
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
    </LanguageProvider>
  );
}

export default App;
